import Form from '@shared/components/forms';
import { html } from '@shared/utils/rendering/html';
import { DateTimePicker } from '@shared/components/forms/form-components/dates';
import ToggleField from '@shared/components/forms/form-components/toggles/toggle-field';
import { wrapWithLayout } from '@shared/components/forms/form-components/utils/layout-wrapper';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState, useMemo } from 'preact/hooks';

import { SystemsFormOptions } from '@core/configs/admin/systems/form/systems-form.config';
import { SystemsFormSchema } from '@core/configs/admin/systems/form/systems-form.schema';

import { usePut } from '@core/utils/hooks/use-put';
import { forwardRef, useImperativeHandle } from 'preact/compat';
import { useAdminContext } from '../../context/admin.context';

const toUtcIsoString = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value.toISOString();

    const localDate = new Date(String(value).replace(' ', 'T'));
    return Number.isNaN(localDate.getTime()) ? value : localDate.toISOString();
};

const toPickerDate = (value) => {
    if (!value) return null;

    const rawValue = String(value);
    const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(rawValue);
    const date =
        value instanceof Date
            ? value
            : new Date(hasOffset ? rawValue : `${rawValue.replace(' ', 'T')}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
};

const EditSystemsForm = forwardRef(({ rowData, fetchSystemsKey }, ref) => {
    const queryClient = useQueryClient();
    const formRef = useRef();

    const { handleSystemUpdate } = useAdminContext();

    const resolvedId = Number(rowData?.remoteSystemId ?? rowData?.systemId);

    const { put, isSubmitting } = usePut(`/systems/${resolvedId}`, {
        successMessage: 'Successfully updated System Details.',
    });

    const [isSystemActive, setIsSystemActive] = useState(rowData?.isActive ?? true);

    useImperativeHandle(ref, () => ({
        reset: () => formRef.current?.reset(),
        submit: (event) => formRef.current?.handleSubmit(event),
        disabled: isSubmitting,
    }));

    const updateSystem = useMutation({
        mutationFn: async (payload) => {
            const res = await put(payload);
            return res?.data ?? res;
        },
        onSuccess: (response) => {
            if (!fetchSystemsKey || !response) return;

            try {
                queryClient.setQueryData(fetchSystemsKey, () => {
                    return response;
                });
                handleSystemUpdate(response);
            } catch (error) {
                console.error('Failed to add API key to cache:', error);
                throw error;
            }
        },
        onSettled: () => {
            if (!fetchSystemsKey) return;
            queryClient.invalidateQueries(fetchSystemsKey);
        },
    });

    const handleSubmit = useCallback(
        async (payload) => {
            try {
                const { systemName, url, isActive, maintenanceMessage, estimatedReturnUtc } =
                    payload;
                await updateSystem.mutateAsync({
                    systemName,
                    url,
                    isActive,
                    maintenanceMessage,
                    estimatedReturnUtc: toUtcIsoString(estimatedReturnUtc),
                });
            } catch (error) {
                console.error('Failed to update system:', error);
                throw error;
            }
        },
        [updateSystem]
    );

    const formData = useMemo(() => {
        if (rowData) {
            return {
                systemName: rowData.systemName,
                url: rowData.url,
                isActive: rowData.isActive,
                ownerUserName: rowData.ownerUserName,
                createdDateUtc: rowData.createdDateUtc,
                maintenanceMessage: rowData.maintenanceMessage ?? '',
                estimatedReturnUtc: toPickerDate(rowData.estimatedReturnUtc),
            };
        }
        return {
            systemName: '',
            url: '',
            isActive: '',
            ownerUserName: '',
            createdDateUtc: '',
            maintenanceMessage: '',
            estimatedReturnUtc: null,
        };
    }, [rowData]);

    const formOptions = useMemo(
        () => ({
            ...SystemsFormOptions,
            layout: {
                ...SystemsFormOptions.layout,
                maintenanceMessage: {
                    ...SystemsFormOptions.layout.maintenanceMessage,
                    disabled: isSystemActive,
                },
                estimatedReturnUtc: {
                    ...SystemsFormOptions.layout.estimatedReturnUtc,
                    disabled: isSystemActive,
                },
            },
            formProps: { ...SystemsFormOptions.formProps, disabled: isSubmitting },
            overrides: {
                ...SystemsFormOptions.overrides,
                isActive: (inputProps, layoutSettings, options, fieldDesc, fieldRef) => {
                    const wrappedInputProps = {
                        ...inputProps,
                        field: {
                            ...inputProps.field,
                            setValue: (value, field) => {
                                const nextIsSystemActive = value === true || value === 'on';
                                setIsSystemActive(nextIsSystemActive);
                                inputProps.field?.setValue(
                                    nextIsSystemActive,
                                    field ?? { name: inputProps.field?.name }
                                );
                            },
                        },
                    };
                    const component = html`<${ToggleField}
                        inputProps=${wrappedInputProps}
                        layoutSettings=${layoutSettings}
                        options=${options}
                        ref=${fieldRef}
                    />`;
                    return { render: wrapWithLayout(component, layoutSettings), fieldRef };
                },
                estimatedReturnUtc: (inputProps, layoutSettings, options, fieldDesc, fieldRef) => {
                    const component = html`<${DateTimePicker}
                        field=${inputProps.field}
                        label=${inputProps.label}
                        errors=${inputProps.errors}
                        dateFormat="yyyy-MM-dd HH:mm"
                        disabled=${layoutSettings.disabled}
                        ref=${fieldRef}
                    />`;
                    return { render: wrapWithLayout(component, layoutSettings), fieldRef };
                },
            },
        }),
        [isSubmitting, isSystemActive]
    );

    return html`
        <div class="card-body">
            <${Form}
                ref=${formRef}
                schema=${SystemsFormSchema}
                onSubmit=${handleSubmit}
                initialValues=${formData}
                options=${formOptions}
            />
        </div>
    `;
});

export default EditSystemsForm;
